import React from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Download, Type, Crop, Palette, Play, Pause, Volume2, VolumeX, Loader2, Sparkles } from 'lucide-react';
import SubtitleStudio from './SubtitleStudio';
// PresetSyncedCaptions is defined below and used by VideoPreview.

export const fmtTime = (s) => {
  if (!s || isNaN(s)) return '00:00';
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

export const EDITOR_COLORS = ['#D4FF00', '#FF5F00', '#00E5FF', '#FF3D7F', '#A855F7'];

// ---------- Captions overlays ----------
// SyncedCaptions consumes the SAME layout payload the ASS renderer used to
// build the burned MP4. `/api/subtitles/preview` returns per-word (cx, cy)
// coordinates on a 1080 × 1920 canvas. Here we scale that canvas to the
// container's actual pixel width and place each word absolutely.
//
// No flex, no gap, no text-align, no browser line-breaking. The backend
// already decided where every word goes.
export const SyncedCaptions = ({ clipId, currentTime }) => {
  const [layout, setLayout] = React.useState(null);
  const overlayRef = React.useRef(null);
  const [scale, setScale] = React.useState(0);

  React.useEffect(() => {
    if (!clipId) return;
    let alive = true;
    api.post('/subtitles/preview', { clip_id: clipId })
      .then((r) => { if (alive) setLayout(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [clipId]);

  React.useEffect(() => {
    if (!overlayRef.current || !layout?.canvas?.w) return;
    const el = overlayRef.current;
    const update = () => setScale(el.clientWidth / layout.canvas.w);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [layout]);

  const emptyOverlay = (
    <div ref={overlayRef} className="absolute inset-0 pointer-events-none" data-testid="editor-synced-captions" />
  );
  if (!layout || !layout.segments?.length || !scale) return emptyOverlay;

  // Resolve visible segment. Extend end by up to 150 ms of readability tail
  // BUT never bleed into the next segment — otherwise two sets of words
  // would render at the same (cx, cy) and become an unreadable smear.
  // Mirrors backend `_word_dialogues_from_layout` timing rule.
  const segs = layout.segments;
  let seg = null;
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    const next = segs[i + 1];
    let end = s.end + 0.15;
    if (next) end = Math.min(end, next.start - 0.02);
    if (currentTime >= s.start && currentTime <= end) { seg = s; break; }
  }
  if (!seg) return emptyOverlay;

  const fontPx = layout.font_size * scale;

  return (
    <div ref={overlayRef} className="absolute inset-0 pointer-events-none" data-testid="editor-synced-captions">
      {seg.words.map((w, i) => {
        const isActive = currentTime >= w.start && currentTime <= w.end;
        return (
          <span
            key={`${seg.start}-${i}`}
            className={'hl-caption-word' + (isActive ? ' is-active' : '')}
            style={{
              position: 'absolute',
              left: `${w.cx * scale}px`,
              top: `${w.cy * scale}px`,
              fontSize: `${fontPx}px`,
              whiteSpace: 'nowrap',
            }}
          >
            {w.w}
          </span>
        );
      })}
    </div>
  );
};

export const StaticCaption = ({ caption, color }) => (
  <div className="absolute inset-x-4 bottom-24 flex flex-wrap justify-center gap-1.5 pointer-events-none" data-testid="editor-caption-overlay">
    {caption.split(/\s+/).filter(Boolean).map((w, i) => (
      <span
        key={`${w}-${i}`}
        className="tiktok-caption text-xl"
        style={{ color: /^[A-Z]{2,}$/.test(w) ? color : '#fff' }}
      >
        {w}
      </span>
    ))}
  </div>
);

// ---------- Preset-driven synced captions (Subtitle Engine V2 overlay) ----------
// Renders the currently-spoken segmented line using the ACTIVE preset. Any
// change of preset / colour / vertical offset in Subtitle Studio flows here
// via the studioPreview + activePreset props → instantly reflected on screen.
export const PresetSyncedCaptions = ({ preview, activePreset, accentColor, currentTime, verticalOffsetPx }) => {
  if (!preview || !preview.lines?.length || !activePreset) return null;
  const line = preview.lines.find((l) => currentTime >= l.start && currentTime <= l.end);
  if (!line) return null;
  const activeIdx = line.words.findIndex((w) => currentTime >= w.start && currentTime <= w.end);

  const primary = activePreset.primary_color || '#FFFFFF';
  const emphasisColor = accentColor || activePreset.accent_color || '#D4FF00';
  const outlinePx = Math.max(1, Math.round((activePreset.outline_width || 6) / 3));
  const shadowPx = Math.max(1, Math.round((activePreset.shadow_offset || 4) / 2));
  const shadow = `${shadowPx}px ${shadowPx}px 0 #000, -${shadowPx}px ${shadowPx}px 0 #000, ${shadowPx}px -${shadowPx}px 0 #000, -${shadowPx}px -${shadowPx}px 0 #000`;
  // Map the 1920 ASS canvas to the container height linearly.
  const bottomPct = Math.max(6, Math.min(80, (verticalOffsetPx || 550) / 1920 * 100));

  return (
    <div
      className="absolute inset-x-4 flex flex-wrap justify-center pointer-events-none"
      style={{ bottom: `${bottomPct}%` }}
      data-testid="editor-preset-captions"
    >
      <div
        className="text-center leading-tight flex flex-wrap justify-center gap-x-2 gap-y-1"
        style={{
          fontFamily: `${activePreset.font}, sans-serif`,
          fontWeight: activePreset.weight === 'regular' ? 400 : activePreset.weight === 'semibold' ? 600 : activePreset.weight === 'bold' ? 700 : 900,
          fontStyle: activePreset.italic ? 'italic' : 'normal',
          letterSpacing: `${(activePreset.letter_spacing || 2) * 0.02}em`,
          textTransform: 'uppercase',
          fontSize: '1.5rem',
        }}
      >
        {line.words.map((w, i) => {
          const isActive = i === activeIdx;
          const isEmph = !!w.emphasis;
          const wordColor = isActive ? emphasisColor : (isEmph ? emphasisColor : primary);
          const scale = isActive ? (activePreset.highlight_style === 'scale' ? 1.25 : 1.12) : 1;
          const glow = (isActive || isEmph) ? `0 0 12px ${emphasisColor}, ${shadow}` : shadow;
          return (
            <span
              key={i}
              className="transition-transform duration-150"
              style={{
                color: wordColor,
                WebkitTextStroke: `${outlinePx}px #000`,
                textShadow: glow,
                transform: `scale(${scale})`,
                display: 'inline-block',
              }}
            >
              {w.w}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Video preview pane ----------
export const VideoPreview = ({
  clip, videoRef, videoUrl, zoom, useSyncedSubs, captionsEnabled = true, caption, color,
  currentTime, duration, playing, muted, exporting,
  onPlay, onPause, onTimeUpdate, onLoadedMetadata, onError,
  togglePlay, toggleMute, exportClip, exportLabel,
  studioPreview, activePreset, verticalOffsetPx,
}) => (
  <div className="rounded-2xl border border-white/5 bg-black overflow-hidden">
    <div className="relative mx-auto" style={{ maxWidth: 380 }}>
      <div className="relative aspect-[9/16] overflow-hidden bg-black">
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: `scale(${zoom[0] / 100})` }}
            playsInline
            onPlay={onPlay}
            onPause={onPause}
            onTimeUpdate={onTimeUpdate}
            onLoadedMetadata={onLoadedMetadata}
            onError={onError}
            data-testid="editor-video"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-center px-6" style={{ background: `linear-gradient(135deg, ${clip.thumbnail_color}, #050505)` }}>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/60 mb-2">Aperçu indisponible</p>
              <p className="text-sm text-white/80">Le fichier MP4 de ce clip n&apos;est pas encore prêt.<br />Relance la génération depuis la page vidéo.</p>
            </div>
          </div>
        )}

        <div className="absolute top-4 left-4 right-4 flex justify-between pointer-events-none">
          <span className="text-[11px] uppercase tracking-widest font-mono text-white/80 drop-shadow-lg">HighlightAI</span>
          <span className="acid-chip px-2 py-0.5 rounded text-[10px] font-bold">{clip.viral_score} VIRAL</span>
        </div>

        {captionsEnabled && (useSyncedSubs && clip.words && clip.words.length > 0 ? (
          <SyncedCaptions clipId={clip.id} currentTime={currentTime} />
        ) : caption ? (
          <StaticCaption caption={caption} color={color} />
        ) : null)}

        <div className="absolute bottom-4 inset-x-4 pointer-events-none">
          <div className="h-1 bg-white/15 rounded-full overflow-hidden">
            <div className="h-full bg-white transition-all" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-white/70 font-mono">
            <span>{fmtTime(currentTime)}</span>
            <span>{fmtTime(duration || clip.duration)}</span>
          </div>
        </div>
      </div>
    </div>

    <div className="flex items-center justify-center gap-3 p-4 border-t border-white/5">
      <Button onClick={togglePlay} variant="outline" className="border-white/10 bg-transparent" disabled={!videoUrl} data-testid="editor-playpause">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </Button>
      <Button onClick={toggleMute} variant="outline" className="border-white/10 bg-transparent" disabled={!videoUrl} data-testid="editor-mute">
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </Button>
      <Button className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold" onClick={exportClip} disabled={!clip.has_file || exporting} data-testid="editor-export">
        {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
        {exporting ? 'Export…' : exportLabel}
      </Button>
    </div>
  </div>
);

// ---------- Right-side configuration tabs ----------
export const EditorPanels = ({
  t, clip, caption, setCaption, color, setColor,
  zoom, setZoom, music, setMusic, useSyncedSubs, setUseSyncedSubs,
  captionsEnabled, setCaptionsEnabled,
  presetId, setPresetId, platform, setPlatform, verticalOffset, setVerticalOffset,
  onPreview,
}) => (
  <Tabs defaultValue="captions" className="rounded-2xl border border-white/5 bg-white/[0.02]">
    <TabsList className="w-full grid grid-cols-2 bg-transparent border-b border-white/5 rounded-none h-12">
      <TabsTrigger value="captions" className="data-[state=active]:bg-[#D4FF00] data-[state=active]:text-black rounded-md" data-testid="editor-tab-captions"><Type className="w-4 h-4" /></TabsTrigger>
      <TabsTrigger value="crop" className="data-[state=active]:bg-[#D4FF00] data-[state=active]:text-black rounded-md" data-testid="editor-tab-crop"><Crop className="w-4 h-4" /></TabsTrigger>
    </TabsList>

    <TabsContent value="captions" className="p-6 space-y-4">
      {/* Master toggle: disable subtitles entirely (no overlay, no burn-in on export) */}
      <div className="rounded-lg border border-white/10 bg-black/40 p-3 flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-xs font-bold uppercase tracking-wider text-white mb-1">{t('ed.subs.label')}</div>
          <p className="text-xs text-zinc-400">{captionsEnabled ? t('ed.subs.on.help') : t('ed.subs.off.help')}</p>
        </div>
        <button
          onClick={() => setCaptionsEnabled((v) => !v)}
          className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-bold transition ${captionsEnabled ? 'bg-[#D4FF00] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
          data-testid="editor-toggle-captions"
        >
          {captionsEnabled ? 'ON' : 'OFF'}
        </button>
      </div>

      <div className={captionsEnabled ? '' : 'opacity-40 pointer-events-none select-none'}>
        {clip.words && clip.words.length > 0 && (
          <div className="rounded-lg border border-[#D4FF00]/30 bg-[#D4FF00]/5 p-3 flex items-start justify-between gap-3 mb-4">
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-wider text-[#D4FF00] mb-1">⚡ Sous-titres synchronisés</div>
              <p className="text-xs text-zinc-400">{clip.words.length} mots détectés par Whisper. Affiche les mots qui s&apos;éclairent au rythme de la voix.</p>
            </div>
            <button
              onClick={() => setUseSyncedSubs((v) => !v)}
              className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-bold transition ${useSyncedSubs ? 'bg-[#D4FF00] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
              data-testid="editor-toggle-synced"
            >
              {useSyncedSubs ? 'ON' : 'OFF'}
            </button>
          </div>
        )}
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{useSyncedSubs && clip.words?.length > 0 ? 'Sous-titre statique (désactivé)' : t('ed.caption.label')}</Label>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="bg-white/5 border-white/10" disabled={useSyncedSubs && clip.words?.length > 0} data-testid="editor-caption-input" />
          <p className="text-xs text-zinc-500 mt-2">Tape des MOTS EN MAJUSCULES pour les colorer avec l&apos;accent.</p>
        </div>
        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block flex items-center gap-2"><Palette className="w-3 h-3" /> {t('ed.color.label')}</Label>
          <div className="flex gap-2">
            {EDITOR_COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} className={`w-9 h-9 rounded-lg border-2 transition ${color === c ? 'border-white scale-110' : 'border-transparent'}`} style={{ background: c }} data-testid={`editor-color-${c.replace('#', '')}`} />
            ))}
          </div>
        </div>
      </div>
    </TabsContent>

    <TabsContent value="crop" className="p-6 space-y-4">
      <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t('ed.zoom.label', { z: zoom[0] })}</Label>
      <Slider value={zoom} onValueChange={setZoom} min={80} max={140} step={5} data-testid="editor-zoom-slider" />
      <p className="text-xs text-zinc-500">{t('ed.zoom.hint')}</p>
    </TabsContent>
  </Tabs>
);

// ---------- Score badges ----------
export const ScorePanel = ({ t, clip }) => (
  <div className="mt-6 p-5 rounded-2xl border border-white/5 bg-white/[0.02]">
    <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-mono mb-3">{t('ed.scores')}</h3>
    <div className="grid grid-cols-3 gap-3 text-center">
      <div><div className="font-heading text-2xl font-black text-[#D4FF00]">{clip.viral_score}</div><div className="text-[10px] uppercase text-zinc-500 mt-1">{t('ed.score.viral')}</div></div>
      <div><div className="font-heading text-2xl font-black">{clip.engagement_score}</div><div className="text-[10px] uppercase text-zinc-500 mt-1">{t('ed.score.engage')}</div></div>
      <div><div className="font-heading text-2xl font-black">{clip.retention_score}</div><div className="text-[10px] uppercase text-zinc-500 mt-1">{t('ed.score.retention')}</div></div>
    </div>
  </div>
);
